import { Transaction } from '@codemirror/state';
import { useIntl } from 'react-intl';

import { XmarkIcon } from '@dynatrace/strato-icons';

import * as styles from './FilterFieldSuffixButtons.sty.js';
import { Button } from '../../../buttons/button/Button.js';
import { Divider } from '../../../layouts/divider/Divider.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { useErrorAwareViewRef } from '../hooks/useErrorAwareViewRef.js';
import { useErrorLogsStore } from '../hooks/useErrorLogsStore.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';
import { filterFieldMessages } from '../messages.js';
import { SuggestionPin } from '../suggestions/SuggestionPin.js';

export function FilterFieldSuffixButtons(
  props: Readonly<{
    value: string;
    disabled?: boolean;
    ariaDisabled?: boolean;
    showError?: boolean;
    readOnly: boolean;
  }>,
) {
  const { value, disabled, ariaDisabled, showError, readOnly } = props;

  const { filterNamespace, filterFieldId, editorView } =
    useFilterFieldContext();

  const intl = useIntl();

  // Error boundary awareness
  const { viewDispatch } = useErrorAwareViewRef({ current: editorView });
  const setAction = useErrorLogsStore((state) => state.setAction);

  if (!value) {
    return null;
  }

  const handleClearButton = () => {
    if (editorView) {
      setAction({
        type: 'clearFilter',
        data: {
          value,
        },
      });

      viewDispatch({
        changes: {
          from: 0,
          to: editorView?.state.doc.length,
          insert: '',
        },
        annotations: Transaction.userEvent.of('clear'),
      });

      // The clear button unmounts once the value is empty, so without this
      // the focus would fall back to <body>. Restore it to the editor.
      editorView.focus();
    }
  };

  return (
    <Flex className={styles.suffix} gap={2}>
      {filterNamespace && (
        <>
          <SuggestionPin
            size="condensed"
            className={styles.button}
            disabled={disabled || readOnly}
            aria-disabled={ariaDisabled}
            filterString={value}
            placement="top"
            fallbackPlacements={['top', 'right', 'left']}
            id={`${filterFieldId}-pin-button`}
            showError={showError}
          />
          <Divider
            orientation="vertical"
            className={styles.divider}
            color={showError ? 'critical' : 'neutral'}
            aria-hidden
          />
        </>
      )}
      <Button
        size="condensed"
        onClick={handleClearButton}
        aria-label={intl.formatMessage(filterFieldMessages.clearFilter)}
        color={showError ? 'critical' : 'neutral'}
        className={styles.button}
        disabled={disabled || readOnly}
        aria-disabled={ariaDisabled}
        data-dt-children-name="ClearAll"
      >
        <Button.Prefix>
          <XmarkIcon />
        </Button.Prefix>
      </Button>
    </Flex>
  );
}
